/**
 * The create-strategy wizard SHELL (W12 Part B, Task B1).
 *
 * Owns the chrome only — rail, step header, first-run note, footer — and
 * renders whatever body the dialog hands it. All selection state, captions and
 * gating stay with `StrategyFormDialog` (data-ownership rule): this component
 * is props-in / JSX-out.
 */

import React from 'react';
import { Box } from '@mui/material';
import type { GuideService } from '@modules/firebase-core/services/GuideService';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import { FirstRunNote } from './FirstRunNote';
import { WizardFooter, type WizardFooterProps } from './WizardFooter';
import { WizardRail } from './WizardRail';
import { WizardStepHeader } from './WizardStepHeader';
import { wizardStepDef, type WizardRailItem, type WizardStepKey } from './wizardSteps';

export interface StrategyCreateWizardProps {
  activeStep: WizardStepKey;
  onStepChange: (key: WizardStepKey) => void;
  /** One entry per step, in `WIZARD_STEPS` order; captions computed by the dialog. */
  rail: WizardRailItem[];
  /** The current step's body. */
  stepBody: React.ReactNode;
  /** Step 1 complete → Next / Skip to review are enabled. */
  pinned: boolean;
  /** Project has zero strategies AND the note is not dismissed. */
  firstRun: boolean;
  onDismissFirstRun: () => void;
  footer: WizardFooterProps;
  guideService: GuideService;
  headerSwitch?: { label: 'Quick create instead' | 'Step-by-step instead'; onClick: () => void };
  'data-testid'?: string;
}

export const StrategyCreateWizard: React.FC<StrategyCreateWizardProps> = ({
  activeStep,
  onStepChange,
  rail,
  stepBody,
  pinned,
  firstRun,
  onDismissFirstRun,
  footer,
  guideService,
  headerSwitch,
  'data-testid': dataTestId,
}) => {
  const step = wizardStepDef(activeStep);

  return (
    <Box
      data-testid={dataTestId ?? STRATEGY_LIST.wizard}
      // MF-2 (fix round 4): the shell grows to the dialog's content box when the
      // dialog bounds it (`SDialog contentFill`, the Insights step) and keeps
      // its AUTOMATIC minimum height — a step that cannot fit spills into the
      // dialog's scroller; a `minHeight: 0` here would let the column shrink
      // under its own content and paint it across the sticky footer.
      sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minWidth: 0 }}
    >
      <WizardRail items={rail} activeStep={activeStep} onStepChange={onStepChange} />

      {/* Owner ruling: content is centred in ONE 800px column. */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 800,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          pt: 3,
          // MF-2: the column GROWS to the shell when the dialog bounds it
          // (`SDialog contentFill`), so a step that fills it (the thesis table)
          // gets the height; its automatic minimum stays — round 4 removed the
          // `minHeight: 0` round 2 added here, which let the column shrink
          // under its content and clip the table rather than fit it. Under the
          // shipped block content box the column is unaffected either way.
          flex: '1 1 auto',
        }}
      >
        {firstRun && <FirstRunNote onDismiss={onDismissFirstRun} />}
        <WizardStepHeader step={step} guideService={guideService} headerSwitch={headerSwitch} />
        {stepBody}
      </Box>

      <WizardFooter {...footer} gated={!pinned} />
    </Box>
  );
};

StrategyCreateWizard.displayName = 'StrategyCreateWizard';
